import { currentLang, pick } from "@kit/lang";

/** Picture themes (core/generator theme ids). The level map's `describe` and the scene both read these. */
export const THEMES: Record<string, { tr: string; en: string }> = {
  animals: { tr: "Hayvanlar", en: "Animals" },
  food: { tr: "Yiyecek", en: "Food" },
  flags: { tr: "Bayraklar", en: "Flags" },
  vehicles: { tr: "Taşıtlar", en: "Vehicles" },
  nature: { tr: "Doğa", en: "Nature" },
  objects: { tr: "Nesneler", en: "Objects" },
  "music-sport": { tr: "Müzik & Spor", en: "Music & Sport" },
};

const STRINGS = {
  name: { tr: "Blok", en: "Block" },
  // Screen-reader instructions for the canvas (TR = the page's meta description in blok/index.html).
  instructions: { tr: "Blokları yerleştir, satırları patlat; altındaki gizli resmi ne kadar az parçada bilirsin?", en: "Place the blocks, clear lines; how few pieces do you need to guess the hidden picture underneath?" },
  title: { tr: "Blok · {n}", en: "Block · {n}" },
  pieces: { tr: "Parça", en: "Pieces" },
  loading: { tr: "Yükleniyor…", en: "Loading…" },
  loadError: { tr: "Resim paketi yüklenemedi\nDokun: tekrar dene", en: "Could not load the pictures\nTap to try again" },
  theme: { tr: "Tema: {theme}", en: "Theme: {theme}" },
  guess: { tr: "Tahmin et", en: "Guess" },
  skipLevel: { tr: "Seviyeyi atla ›", en: "Skip level ›" },
  revealed: { tr: "Açılan {n} / {total}", en: "Open {n} / {total}" },
  selected: { tr: "Seçili → noktaya dokun", en: "Selected → tap a dot" },
  firstMove: { tr: "Sürükle ya da seç", en: "Drag or tap a piece" },
  score: { tr: "Puan {score}  ·  {lines} çizgi", en: "Score {score}  ·  Lines {lines}" },
  firstHint: { tr: "Resmi açtıkça istediğin an tahmin et", en: "Guess at any time" },
  streak: { tr: "Seri ×{m}", en: "Streak ×{m}" },
  combo2: { tr: "Çifte!", en: "Double!" },
  combo3: { tr: "Üçlü!", en: "Triple!" },
  combo4: { tr: "Dörtlü!", en: "Quad!" },
  combo5: { tr: "Beşli!", en: "Penta!" },
  combo6: { tr: "Efsane!", en: "Legendary!" },
  perfect: { tr: "Tahta temiz!", en: "Board clear!" },
  question: { tr: "Bu ne? · {theme}", en: "What is it? · {theme}" },
  guessHint: { tr: "Yanlış tahmin −100 puan. Doğru bilirsen seviye biter.", en: "Wrong guess −100 points. Get it right to finish the level." },
  correct: { tr: "Doğru! {pieces} parçada bildin", en: "Correct! Solved in {pieces} {unit}" },
  repeat: { tr: "Bunu zaten denedin", en: "You already tried that" },
  wrong: { tr: "Yanlış · −{penalty} puan", en: "Wrong · −{penalty} points" },
  won: { tr: "Bildin!", en: "You got it!" },
  wonOpen: { tr: "{n} / {total} açık", en: "{n} / {total} open" },
  wonSub: { tr: "{pieces} parçada bildin", en: "Solved in {pieces} {unit}" },
  wrongCount: { tr: "{wrong} yanlış tahmin", en: "Wrong guesses: {wrong}" },
  noWrong: { tr: "Yanlış tahmin yok", en: "No wrong guesses" },
  points: { tr: "Puan {score}", en: "Score {score}" },
  bestStreak: { tr: "En iyi seri ×{m}", en: "Best streak ×{m}" },
  newBest: { tr: "Yeni rekor", en: "New best" },
  best: { tr: "Rekor: {best}", en: "Best: {best}" },
  share: { tr: "Paylaş", en: "Share" },
  shared: { tr: "Hazır", en: "Done" },
  // {again} (shareAgain) states a retry openly: attempt > 1 already knew the picture, so the number stays honest.
  shareText: { tr: "Blok'ta {pieces} parçada bildim{wrong} · Seviye {n}{again}", en: "Block: I got it in {pieces} {unit}{wrong} · Level {n}{again}" },
  shareWrong: { tr: " ({wrong} yanlış)", en: " ({wrong} wrong)" },
  shareAgain: { tr: " · {attempt}. deneme", en: " · attempt {attempt}" },
  lost: { tr: "Yer kalmadı", en: "Out of room" },
  noFit: { tr: "Hiçbir parça sığmıyor", en: "No piece fits" },
  lostStats: { tr: "{placed} parça · {open}/{total} açıldı", en: "{placed} {unit} · {open}/{total} opened" },
  skipHelp: { tr: "Takıldıysan ücretsiz atlayabilirsin", en: "Stuck? You can skip for free" },
  retry: { tr: "Tekrar dene", en: "Try again" },
  skip: { tr: "Atla", en: "Skip" },
  // {unit} after a piece count: English needs "1 piece" / "2 pieces"; Turkish templates inflect the noun themselves.
  unitOne: { tr: "parça", en: "piece" },
  unitMany: { tr: "parça", en: "pieces" },
} as const;

export type Key = keyof typeof STRINGS;
export function t(key: Key, vars: Record<string, string | number> = {}): string {
  return pick(currentLang(), STRINGS[key]).replace(/\{(\w+)\}/g, (_m, k: string) => String(vars[k] ?? ""));
}

/** The {unit} word for a count of pieces. */
export function pieceUnit(n: number): string {
  return t(n === 1 ? "unitOne" : "unitMany");
}

/** Theme name in the player's language; `fallback` when the id is unknown. */
export function themeName(theme: string, fallback = theme): string {
  const name = THEMES[theme];
  return name ? pick(currentLang(), name) : fallback;
}
